"use client";

import { useAnswers } from "../../hooks/use-answers";
import { useCopy } from "../../hooks/use-copy";
import { getAnswerText } from "../../utils/get-answer-text";
import { useLeadFlowRuntime } from "../LeadFlowRuntimeContext";

const ANSWER_TITLES = {
  careFor: "Who needs care",
  careType: "Care type",
  urgency: "Urgency",
} as const;

/** The postal code's title follows the site's country. */
type SummaryFieldName = keyof typeof ANSWER_TITLES | "postalCode";

type AnswerSummaryItemProps = {
  item: { answers?: string[] | null; heading?: string | null };
};

function isSummaryFieldName(value: string): value is SummaryFieldName {
  return value === "postalCode" || Object.hasOwn(ANSWER_TITLES, value);
}

/** Lists the picked answers the visitor has given; unanswered ones are skipped. */
function AnswerSummaryItem({ item }: AnswerSummaryItemProps) {
  const answers = useAnswers();
  const { answerLabels } = useLeadFlowRuntime();
  const { postalCodeLabel } = useCopy();

  const rows = (item.answers ?? [])
    .filter(isSummaryFieldName)
    .flatMap((fieldName) => {
      const answerText = getAnswerText(fieldName, answers, answerLabels);
      return answerText ? [{ answerText, fieldName }] : [];
    });

  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-2">
      {item.heading ? <h3 className="font-bold">{item.heading}</h3> : null}
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {rows.map(({ answerText, fieldName }) => (
          <div className="contents" key={fieldName}>
            <dt className="text-sm">
              {fieldName === "postalCode"
                ? postalCodeLabel
                : ANSWER_TITLES[fieldName]}
            </dt>
            <dd className="font-bold text-sm">{answerText}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export { AnswerSummaryItem };
